// GAVION ai follow-up (spec §6 demo): the FakeRest twin of the follow-up plans (71_gavion_ai_follow_up.sql): no network,
// no model. The days come from the same cadence as SQL (planTouchDays, Quebec holidays, 10:00 Toronto), the draft from
// the fake drafter for the touch's angle, with a website detail from a fixture (demo/fakeWebsite.ts). In the demo the
// first touch is written at once (the real tick writes a touch one hour before it is due), so « Suivi IA » and Brouillons
// fill up right after the call. Mirrors the rep actions' rules and codes (forbidden, not_planned, not_business_day…).
import type { RecordStore } from "../../dialer/api/recordStore";
import { fakeDraft } from "../../../../../supabase/functions/_shared/ai/fakeDrafter.ts";
import {
  FOLLOW_UP_CALL_OUTCOMES,
  angleFor,
  followUpSettingsOf,
  openDay,
  planTouchDays,
  type FollowUpAngle,
  type FollowUpSituation,
} from "../../../../../supabase/functions/_shared/ai/followUp.ts";
import type { TimelineRow } from "../../../../../supabase/functions/_shared/ai/types.ts";
import { quebecHolidaysAround } from "../../../../../supabase/functions/_shared/email/quebecHolidays.ts";
import { demoWebsiteDetail } from "../../demo/fakeWebsite";
import { buildFakeContext } from "./fakeAiContext";
import { AiApiError } from "./types";

type Row = Record<string, unknown> & { id: number };
const same = (a: unknown, b: unknown) => String(a) === String(b);
const DAY = 86_400_000;
const TZ = "America/Toronto";
const OPEN = ["planned", "drafting", "proposed"];
/** Outcomes that neither plan nor stop a plan (ruling 2: the dialer redials unanswered leads). */
const NEUTRAL_OUTCOMES = ["no_answer", "skipped", "unlogged"];
/** The Toronto day of the lead's next callback, when it is still ahead (SQL: companies.next_callback_at > now()). */
const callbackDay = (company: Row | undefined, now: Date): string | null => {
  const at = company?.next_callback_at;
  if (typeof at !== "string" || !(Date.parse(at) > now.getTime())) return null;
  return torontoDay(new Date(at));
};

export type FakeFollowUpDeps = {
  store: RecordStore;
  me: number;
  isAdmin: boolean;
  now: () => Date;
  timeline: (companyId: number) => Promise<TimelineRow[]>;
};

/** The Toronto calendar day of an instant (YYYY-MM-DD). */
export const torontoDay = (d: Date): string =>
  d.toLocaleDateString("en-CA", { timeZone: TZ });
/** 10:00 Toronto on `day`, as an ISO instant (EDT or EST, whichever applies that day). */
export function tenAmToronto(day: string): string {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: TZ,
      hour: "numeric",
      hourCycle: "h23",
    }).format(new Date(`${day}T12:00:00Z`)),
  );
  return new Date(
    Date.parse(`${day}T10:00:00Z`) + (12 - hour) * 3_600_000,
  ).toISOString();
}
const addDays = (day: string, n: number): string =>
  new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);

const list = (d: FakeFollowUpDeps, r: string) =>
  d.store.list<Row>(r).catch(() => [] as Row[]);
const touchesOf = async (d: FakeFollowUpDeps, planId: unknown) =>
  (await list(d, "ai_follow_up_touches"))
    .filter((t) => same(t.plan_id, planId))
    .sort((a, b) => Number(a.position) - Number(b.position));

/** The twin of trg_ai_drafts_follow_up: a touch's draft decided → the touch sent or skipped; a plan with nothing left is done. */
export async function fakeTouchDecided(
  store: RecordStore,
  draftId: number,
  status: string,
): Promise<void> {
  const touches = await store
    .list<Row>("ai_follow_up_touches")
    .catch(() => [] as Row[]);
  const t = touches.find(
    (x) => same(x.draft_id, draftId) && x.status === "proposed",
  );
  if (!t) return;
  await store.update("ai_follow_up_touches", t.id, {
    status: status === "sent" ? "sent" : "skipped",
    // SQL trg_ai_drafts_follow_up: the draft's status is the skipped touch's error
    error: status === "sent" ? null : status,
  });
  const left = touches.some(
    (x) =>
      same(x.plan_id, t.plan_id) &&
      !same(x.id, t.id) &&
      OPEN.includes(String(x.status)),
  );
  const plan = await store.get<Row>("ai_follow_up_plans", Number(t.plan_id));
  if (!left && plan?.status === "active")
    await store.update("ai_follow_up_plans", plan.id, { status: "done" });
}

async function stopPlan(
  d: FakeFollowUpDeps,
  plan: Row,
  reason: string,
): Promise<void> {
  const at = d.now().toISOString();
  await d.store.update("ai_follow_up_plans", plan.id, {
    status: "stopped",
    stop_reason: reason,
    ended_at: at,
  });
  for (const t of await touchesOf(d, plan.id)) {
    if (!OPEN.includes(String(t.status))) continue;
    await d.store.update("ai_follow_up_touches", t.id, { status: "cancelled" });
    const draft =
      t.draft_id == null
        ? null
        : await d.store.get<Row>("ai_email_drafts", Number(t.draft_id));
    if (draft?.status === "pending")
      await d.store.update("ai_email_drafts", draft.id, {
        status: "superseded",
        decided_at: at,
      });
  }
}

async function ownPlan(d: FakeFollowUpDeps, planId: unknown): Promise<Row> {
  const plan = await d.store.get<Row>("ai_follow_up_plans", Number(planId));
  if (!plan) throw new AiApiError("not_found");
  if (!same(plan.sales_id, d.me) && !d.isAdmin)
    throw new AiApiError("forbidden");
  return plan;
}

/** Write a touch now (demo): the fake drafter for its angle; one open proposed draft per lead, as in SQL. */
export async function demoWriteTouch(
  d: FakeFollowUpDeps,
  touchId: number,
): Promise<void> {
  const t = await d.store.get<Row>("ai_follow_up_touches", touchId);
  if (!t) throw new AiApiError("not_found");
  const plan = await d.store.get<Row>("ai_follow_up_plans", Number(t.plan_id));
  if (!plan || plan.status !== "active") throw new AiApiError("not_active");
  const company = (await list(d, "companies")).find((c) =>
    same(c.id, plan.company_id),
  );
  const input = await buildFakeContext(d.store, d.timeline, {
    companyId: Number(plan.company_id),
    contactId: plan.contact_id == null ? null : Number(plan.contact_id),
    dealId: plan.deal_id == null ? null : Number(plan.deal_id),
    salesId: Number(plan.sales_id),
    trigger: "follow_up",
    instruction: null,
    clientMessage: null,
    recipientEmail: String(plan.to_email),
    sourceType: "call",
    sourceId: Number(plan.source_id),
  });
  if (!input) throw new AiApiError("lead_not_found");
  const all = await touchesOf(d, plan.id);
  const followUp = {
    situation: plan.situation as FollowUpSituation,
    angle: t.angle as FollowUpAngle,
    position: Number(t.position),
    total: all.length,
    websiteDetail: demoWebsiteDetail(company),
  };
  const now = d.now();
  for (const x of await list(d, "ai_email_drafts"))
    if (
      same(x.company_id, plan.company_id) &&
      x.status === "pending" &&
      x.trigger !== "on_demand"
    ) {
      await d.store.update("ai_email_drafts", x.id, {
        status: "superseded",
        decided_at: now.toISOString(),
      });
      await fakeTouchDecided(d.store, x.id, "superseded");
    }
  const out = fakeDraft({ ...input, followUp });
  const draft = await d.store.create<Row>("ai_email_drafts", {
    company_id: plan.company_id,
    contact_id: plan.contact_id ?? null,
    deal_id: plan.deal_id ?? null,
    sales_id: plan.sales_id,
    trigger: "follow_up",
    source_type: "follow_up_touch",
    source_id: t.id,
    to_email: plan.to_email,
    subject: out.subject,
    body: out.body,
    rationale: out.rationale,
    language: out.language,
    status: "pending",
    edited: false,
    model: "claude-sonnet-5-5",
    follow_up_position: followUp.position,
    follow_up_total: followUp.total,
    created_at: now.toISOString(),
    expires_at: new Date(now.getTime() + 3 * DAY).toISOString(),
  });
  await d.store.update("ai_follow_up_touches", t.id, {
    status: "proposed",
    draft_id: draft.id,
  });
}

/** A logged call (DialerScreen, demo): plan its follow-up (replacing the open one) and write the first touch. */
export async function demoStartPlan(
  d: FakeFollowUpDeps,
  i: { companyId: number; callId: number; outcome: string },
): Promise<boolean> {
  // SQL private.ai_follow_up_on_call (ruling 2): no_answer, skipped and unlogged change nothing — except on a
  // correction of the same call, which stops the plan that call started (final review I1); another reached outcome
  // stops the open plan (new_outcome); a qualifying one replaces it (with follow-ups switched off no plan is open: the
  // switch stopped them, fakeFollowUpsSwitched).
  if (NEUTRAL_OUTCOMES.includes(i.outcome)) {
    for (const p of await list(d, "ai_follow_up_plans"))
      if (
        p.status === "active" &&
        p.source_type === "call" &&
        same(p.source_id, i.callId)
      )
        await stopPlan(d, p, "new_outcome");
    return false;
  }
  const open = (await list(d, "ai_follow_up_plans")).filter(
    (p) => same(p.company_id, i.companyId) && p.status === "active",
  );
  if (!Object.hasOwn(FOLLOW_UP_CALL_OUTCOMES, i.outcome)) {
    for (const p of open) await stopPlan(d, p, "new_outcome");
    return false;
  }
  const situation = FOLLOW_UP_CALL_OUTCOMES[i.outcome];
  const cfg = (await list(d, "configuration")).find((c) => same(c.id, 1));
  const settings = followUpSettingsOf(cfg?.config);
  if (!settings.enabled) return false;
  for (const p of open) await stopPlan(d, p, "replaced");
  const input = await buildFakeContext(d.store, d.timeline, {
    companyId: i.companyId,
    contactId: null,
    dealId: null,
    salesId: d.me,
    trigger: "follow_up",
    instruction: null,
    clientMessage: null,
    recipientEmail: null,
    sourceType: "call",
    sourceId: i.callId,
  });
  if (!input || !input.recipient.email) return false;
  const now = d.now();
  const company = (await list(d, "companies")).find((c) =>
    same(c.id, i.companyId),
  );
  const { days, preCallback } = planTouchDays(
    situation,
    torontoDay(now),
    callbackDay(company, now),
    settings.cadences[situation],
    quebecHolidaysAround(now),
  );
  const plan = await d.store.create<Row>("ai_follow_up_plans", {
    company_id: i.companyId,
    sales_id: d.me,
    contact_id: input.recipient.contactId,
    deal_id: input.deal?.id ?? null,
    to_email: input.recipient.email,
    source_type: "call",
    source_id: i.callId,
    situation,
    status: "active",
    stop_reason: null,
    created_at: now.toISOString(),
    ended_at: null,
  });
  for (const [k, day] of days.entries())
    await d.store.create("ai_follow_up_touches", {
      plan_id: plan.id,
      position: k + 1,
      angle: angleFor(
        situation,
        k + 1,
        days.length,
        preCallback && k === days.length - 1,
      ),
      due_at: tenAmToronto(day),
      status: "planned",
      draft_id: null,
      manual: false,
      attempts: 0,
      error: null,
    });
  const [first] = await touchesOf(d, plan.id);
  if (first) await demoWriteTouch(d, first.id);
  return true;
}

export async function demoCancelPlan(
  d: FakeFollowUpDeps,
  planId: number,
): Promise<void> {
  const plan = await ownPlan(d, planId);
  if (plan.status !== "active") throw new AiApiError("not_active");
  await stopPlan(d, plan, "rep_cancel");
}

export async function demoMoveTouch(
  d: FakeFollowUpDeps,
  touchId: number,
  day: string,
): Promise<string> {
  const t = await d.store.get<Row>("ai_follow_up_touches", touchId);
  if (!t) throw new AiApiError("not_found");
  const plan = await ownPlan(d, t.plan_id);
  if (plan.status !== "active" || t.status !== "planned")
    throw new AiApiError("not_planned");
  const today = torontoDay(d.now());
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day <= today)
    throw new AiApiError("in_past");
  if (day > addDays(today, 60)) throw new AiApiError("too_far");
  if (!openDay(day, new Set(quebecHolidaysAround(d.now()))))
    throw new AiApiError("not_business_day");
  const company = (await list(d, "companies")).find((c) =>
    same(c.id, plan.company_id),
  );
  if (day === callbackDay(company, d.now()))
    throw new AiApiError("callback_day");
  const all = await touchesOf(d, plan.id);
  const dayOf = (x: Row) => torontoDay(new Date(String(x.due_at)));
  const prev = all.filter(
    (x) =>
      Number(x.position) < Number(t.position) &&
      ["planned", "drafting", "proposed", "sent"].includes(String(x.status)),
  );
  const next = all.filter(
    (x) => Number(x.position) > Number(t.position) && x.status === "planned",
  );
  if (prev.some((x) => day <= dayOf(x)) || next.some((x) => day >= dayOf(x)))
    throw new AiApiError("out_of_order");
  const due = tenAmToronto(day);
  await d.store.update("ai_follow_up_touches", t.id, {
    due_at: due,
    manual: true,
  });
  return due;
}

export async function demoWriteNow(
  d: FakeFollowUpDeps,
  touchId: number,
): Promise<void> {
  const t = await d.store.get<Row>("ai_follow_up_touches", touchId);
  if (!t) throw new AiApiError("not_found");
  const plan = await ownPlan(d, t.plan_id);
  if (plan.status !== "active" || t.status !== "planned")
    throw new AiApiError("not_planned");
  const all = await touchesOf(d, plan.id);
  if (all.some((x) => x.status === "drafting" || x.status === "proposed"))
    throw new AiApiError("busy");
  if (
    all.some(
      (x) => x.status === "planned" && Number(x.position) < Number(t.position),
    )
  )
    throw new AiApiError("not_next");
  await d.store.update("ai_follow_up_touches", t.id, { manual: true });
  await demoWriteTouch(d, t.id);
}

/**
 * The twin of trg_configuration_ai_follow_up (owner ruling, final review I2): « Suivis IA activés » saved off stops
 * every active plan at once (disabled); turning it back on restarts none. Returns the number of plans stopped.
 */
export async function fakeFollowUpsSwitched(
  store: RecordStore,
  config: unknown,
  now: Date,
): Promise<number> {
  if (followUpSettingsOf(config).enabled) return 0;
  const d: FakeFollowUpDeps = {
    store,
    me: 0,
    isAdmin: true,
    now: () => now,
    timeline: async () => [],
  };
  let n = 0;
  for (const p of await list(d, "ai_follow_up_plans"))
    if (p.status === "active") {
      await stopPlan(d, p, "disabled");
      n += 1;
    }
  return n;
}
